import './styling/travelStyling.scss';

import { FC, useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Alert, Button, Link, Text, Title } from '@gjensidige/builders-components';
import { Brick } from '@gjensidige/claims-lib-shared';

import { postEquinorTravelForm, postFormStartedCount } from '../../../api';
import {
  CountRequest,
  equinorTravelForm,
  EquinorTravelValues,
  FormType,
} from '../../../utils/types';
import Cancellation from './Cancellation';
import { Confirmation } from './Confirmation/Confirmation';
import DamagedOrLostBelongings from './DamagedOrLostBelongings';
import DelayedLuggage from './DelayedLuggage';
import DelayedOrCancelledTransportation from './DelayedOrCancelledTransportation';
import Medical from './Medical';
import Other from './Other';

interface FormContainerProps {
  path: string;
}

export const FormContainer: FC<FormContainerProps> = ({ path }) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EquinorTravelValues>({
    resolver: zodResolver(equinorTravelForm),
  });
  const [confirmationPageReady, setConfirmationPageReady] = useState<boolean>(false);
  const [postFormError, setPostFormError] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    postFormStartedCount(getCountRequest());
  }, []);

  const formRenderer = () => {
    if (path.endsWith('cancellation')) {
      return <Cancellation control={control} errors={errors} />;
    }
    if (path.endsWith('medical')) {
      return <Medical control={control} errors={errors} />;
    }
    if (path.endsWith('delayed-luggage')) {
      return <DelayedLuggage control={control} errors={errors} />;
    }
    if (path.endsWith('other')) {
      return <Other control={control} errors={errors} />;
    }
    if (path.endsWith('damaged-or-lost-personal-belongings')) {
      return <DamagedOrLostBelongings control={control} errors={errors} />;
    }
    if (path.endsWith('delayed-or-cancelled-transportation')) {
      return <DelayedOrCancelledTransportation control={control} errors={errors} />;
    }
  };

  const getCountRequest = (): CountRequest => ({
    type: getFormType()!,
  });

  const getFormType = () => {
    if (path.endsWith('cancellation')) {
      return FormType.CANCELLATION;
    }
    if (path.endsWith('medical')) {
      return FormType.MEDICAL;
    }
    if (path.endsWith('delayed-luggage')) {
      return FormType.DELAYED_LUGGAGE;
    }
    if (path.endsWith('other')) {
      return FormType.OTHER;
    }
    if (path.endsWith('damaged-or-lost-personal-belongings')) {
      return FormType.DAMAGED_LOST_BELONGINGS;
    }
    if (path.endsWith('delayed-or-cancelled-transportation')) {
      return FormType.DELAYED_OR_CANCELLED_TRANSPORTATION;
    }
  };

  const onSubmit: SubmitHandler<EquinorTravelValues> = async (data) => {
    setIsSubmitting(true);
    setPostFormError(false);
    const formType = getFormType();
    if (formType) {
      postEquinorTravelForm(data, formType)
        .then((response) => {
          if (response === 'sent') {
            setConfirmationPageReady(true);
            setIsSubmitting(false);
          }
        })
        .catch(() => {
          setPostFormError(true);
          setIsSubmitting(false);
        });
    }
  };

  if (confirmationPageReady) {
    return <Confirmation />;
  }

  return (
    <Brick variant="plain">
      <Button
        variant="action"
        arrow="left"
        className="back-button"
        href="https://www.gjensidige.no/meld-skade/privat/reise/claims-equinor-eng"
      >
        Back
      </Button>
      <form
        method="post"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(onSubmit);
        }}
      >
        {formRenderer()}
        <Alert variant="outlined" className="alert">
          <Title>After you submit the claim</Title>
          Once submitted, one of our claim handlers will review your claim and get back to you. We
          will ask you if we need receipts or other documents.
        </Alert>
        <Button
          variant="critical"
          type="submit"
          loading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          Submit
        </Button>
        {postFormError && (
          <Alert severity="error" className="error-alert">
            There was an error sending the form. Please try again later.
          </Alert>
        )}
        <Text className="text-spacing">
          Gjensidige uses your personal information to process the claim.{' '}
          <Link href="https://www.gjensidige.no/personvern-og-sikkerhet/personopplysninger">
            Read about how we manage personal information.
          </Link>
        </Text>
      </form>
    </Brick>
  );
};
